// PWA Service Worker Hook
// Integrates with vite-plugin-pwa for silent background updates.
// When a new service worker is available it is activated immediately in the
// background and the page is reloaded on the next client-side navigation so
// users get the update without any disruptive prompts.

import { useCallback, useEffect, useRef, useState } from 'react';

interface PWAUpdateProps {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

export function usePWA({ onNeedRefresh, onOfflineReady }: PWAUpdateProps = {}) {
  const [needRefresh, setNeedRefresh] = useState(false);
  // Use a ref so navigation event listeners always read the latest value
  // without needing to be re-registered when state changes.
  const needRefreshRef = useRef(false);
  const isUpdatingRef = useRef(false);

  // Keep the ref in sync with state.
  useEffect(() => {
    needRefreshRef.current = needRefresh;
  }, [needRefresh]);

  const updateApp = useCallback(() => {
    // Prevent multiple simultaneous updates
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(async (registration) => {
        if (registration?.waiting) {
          // Tell the waiting service worker to skip waiting and become active
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Clear all caches to ensure fresh content
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map((cacheName) => caches.delete(cacheName)),
          );
        }
      });
    }

    // Small delay to allow cache clearing to complete before reload
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    let updateInterval: NodeJS.Timeout;
    let checkForUpdates: (() => void) | undefined;

    // When an update is detected, activate the new SW in the background
    // immediately and mark that a refresh is pending.
    const handleUpdateAvailable = () => {
      setNeedRefresh(true);
      needRefreshRef.current = true;
      onNeedRefresh?.();

      // Activate the waiting SW silently so it is ready for the next reload.
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration?.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      });
    };

    // Reload on the next client-side navigation so the user picks up the new
    // version without being interrupted mid-session.
    const handleNavigation = () => {
      if (needRefreshRef.current && !isUpdatingRef.current) {
        isUpdatingRef.current = true;
        // Short delay so the navigation URL updates before the reload.
        setTimeout(() => {
          window.location.reload();
        }, 50);
      }
    };

    // Intercept React Router (history API) navigations.
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args) => {
      originalPushState(...args);
      handleNavigation();
    };

    history.replaceState = (...args) => {
      originalReplaceState(...args);
      handleNavigation();
    };

    // Also handle browser back/forward navigation.
    window.addEventListener('popstate', handleNavigation);

    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        registration.addEventListener('updatefound', handleUpdateAvailable);

        // Handle the case where a SW is already waiting (e.g. after a hard refresh)
        if (registration.waiting) {
          handleUpdateAvailable();
        }

        // Check for updates every 5 minutes while the page is visible.
        checkForUpdates = () => {
          if (
            document.visibilityState === 'visible' &&
            !isUpdatingRef.current
          ) {
            registration.update().catch(() => {
              // Silently fail if the update check fails (e.g. offline)
            });
          }
        };

        updateInterval = setInterval(checkForUpdates, 300000); // 5 minutes
        document.addEventListener('visibilitychange', checkForUpdates);
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (checkForUpdates) {
        document.removeEventListener('visibilitychange', checkForUpdates);
      }
      window.removeEventListener('popstate', handleNavigation);
      // Restore original history methods
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [onNeedRefresh, onOfflineReady, updateApp]);

  return {
    needRefresh,
    updateApp,
  };
}
